import React from 'react';
import { Nullable } from 'global';
import { Stack, Box } from '@mui/material';
import { Charge } from 'modules/equipment/types';
import { DiscountType } from '@trnsact/trnsact-shared-types';
import { ListingForm } from 'components/shared/TableListing/types';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { CurrencyInputField, RadioGroupField, InterestInputField } from 'components/form';
import { chargeTypeOptions } from '../../../../../constants/form';
import { AttachmentChargeWithTypeValue } from './AttachmentChargeWithTypeValue';
import { QuoteChargeListingEntity, QuoteChargeType, QuoteEquipmentWithCharge } from '../../../../../types';

interface Props {
  rowIndex: number;
  type: keyof Charge;
  onSyncEquipment?: (equipment: Nullable<QuoteEquipmentWithCharge>) => Promise<void>;
}

export const ChargeValueWithTypeCell = ({ type, rowIndex, onSyncEquipment }: Props) => {
  const { control } = useFormContext<ListingForm<QuoteChargeListingEntity>>();

  const { type: cellType, equipment } = useWatch({ control, name: `bodyFields.${rowIndex}` });

  const fields: Record<
    QuoteChargeType,
    Record<'valuePath' | 'typePath', FieldPath<ListingForm<QuoteChargeListingEntity>>>
  > = {
    [QuoteChargeType.Equipment]: {
      valuePath: `bodyFields.${rowIndex}.equipment.price.${type}`,
      typePath: `bodyFields.${rowIndex}.equipment.price.${type}Type` as any,
    },
    [QuoteChargeType.Invoice]: {
      valuePath: `bodyFields.${rowIndex}.charge.${type}`,
      typePath: `bodyFields.${rowIndex}.charge.${type}Type` as any,
    },
  };

  const handleSyncEquipment = async () => {
    if (typeof onSyncEquipment !== 'function') return;
    await onSyncEquipment(equipment);
  };

  const fieldType = useWatch({ control, name: fields[cellType].typePath });

  const fieldMapper: Record<DiscountType, any> = {
    [DiscountType.Percent]: (
      <InterestInputField
        control={control}
        name={fields[cellType].valuePath}
        textFieldProps={{ variant: 'outlined', onBlur: handleSyncEquipment }}
      />
    ),
    [DiscountType.Flat]: (
      <CurrencyInputField
        allowNegative
        control={control}
        name={fields[cellType].valuePath}
        textFieldProps={{ variant: 'outlined', onBlur: handleSyncEquipment }}
      />
    ),
  };

  return (
    <Stack width="100%" height="100%" justifyContent="flex-end" gap="0.5rem">
      <Box>
        {fieldMapper[fieldType as DiscountType]}
        <RadioGroupField
          row
          control={control}
          options={chargeTypeOptions}
          onBlur={handleSyncEquipment}
          name={fields[cellType].typePath}
        />
      </Box>

      <RenderWithCondition condition={!!equipment?.attachments}>
        {equipment?.attachments?.map((_, index) => (
          <AttachmentChargeWithTypeValue key={index} type={type} rowIndex={rowIndex} attachmentIndex={index} />
        ))}
      </RenderWithCondition>
    </Stack>
  );
};
